import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import { MANIFESTO, STATS, IMAGES, OFFICE } from "@/lib/site";

function PageHeader() {
  return (
    <section className="relative overflow-hidden border-b border-white/10 pt-40 pb-24">
      <div className="absolute inset-0 grid-lines opacity-60" aria-hidden="true" />
      <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
        <Reveal>
          <span className="overline text-cyber">About Core Loop Tech</span>
        </Reveal>
        <Reveal i={1}>
          <h1 data-testid="about-heading" className="mt-6 max-w-4xl font-display text-5xl font-semibold leading-[0.95] tracking-tighter text-white sm:text-6xl lg:text-7xl">
            A studio built on <span className="text-cyber">loops</span>, not launches.
          </h1>
        </Reveal>
        <Reveal i={2}>
          <p className="mt-8 max-w-2xl font-body text-lg leading-relaxed text-white/55">
            Core Loop Tech is a Hong Kong–based engineering studio. We partner with
            ambitious teams to design intelligent software, unify their data and
            defend their infrastructure — then keep refining it long after go-live.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function Spotlight() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [-40, 40]);

  return (
    <section ref={ref} className="relative mx-auto max-w-[1400px] px-6 py-24 lg:px-10">
      <div className="relative overflow-hidden rounded-3xl border border-white/10">
        <motion.img
          style={{ y }}
          src={IMAGES.manifesto}
          alt="Hong Kong skyline at night"
          className="h-[420px] w-full scale-110 object-cover sm:h-[540px]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink-950 via-ink-950/40 to-transparent" aria-hidden="true" />
        <div className="absolute bottom-0 left-0 p-8 lg:p-14">
          <span className="overline text-cyber">Rooted in Kowloon</span>
          <p className="mt-4 max-w-xl font-display text-2xl font-medium leading-snug tracking-tight text-white sm:text-3xl">
            Operating from Hung Hom, Hong Kong — a crossroads of global commerce and
            deep engineering talent.
          </p>
        </div>
      </div>
    </section>
  );
}

function Manifesto() {
  return (
    <section data-testid="manifesto" className="mx-auto max-w-[1400px] px-6 py-16 lg:px-10 lg:py-28">
      <Reveal>
        <span className="overline text-cyber">The manifesto</span>
        <h2 className="mt-5 max-w-3xl font-display text-4xl font-semibold leading-tight tracking-tighter text-white sm:text-5xl">
          Three beliefs that shape everything we build.
        </h2>
      </Reveal>

      <div className="mt-16 divide-y divide-white/10 border-t border-white/10">
        {MANIFESTO.map((m, i) => (
          <Reveal key={m.no} i={i}>
            <div className="group grid gap-6 py-12 md:grid-cols-12 md:items-start">
              <div className="md:col-span-3">
                <span className="font-display text-6xl font-bold tracking-tighter text-white/10 transition-colors duration-500 group-hover:text-cyber/70">
                  {m.no}
                </span>
              </div>
              <h3 className="font-display text-2xl font-semibold leading-tight tracking-tight text-white md:col-span-5 lg:text-3xl">
                {m.title}
              </h3>
              <p className="font-body text-base leading-relaxed text-white/55 md:col-span-4">
                {m.body}
              </p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function StatsBand() {
  return (
    <section className="border-y border-white/10 bg-ink-900">
      <div className="mx-auto grid max-w-[1400px] grid-cols-2 divide-x divide-white/10 md:grid-cols-4">
        {STATS.map((s, i) => (
          <Reveal key={s.label} i={i} className="px-6 py-14 lg:px-10">
            <div className="font-display text-4xl font-bold tracking-tighter text-cyber sm:text-5xl">{s.value}</div>
            <div className="mt-2 font-body text-sm text-white/50">{s.label}</div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function CTA() {
  return (
    <section className="mx-auto max-w-[1400px] px-6 py-28 lg:px-10">
      <Reveal className="relative overflow-hidden rounded-3xl border border-cyber/20 bg-ink-850 p-10 lg:p-16">
        <div className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-cyber/20 blur-[100px]" aria-hidden="true" />
        <div className="relative flex flex-col gap-8 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="font-display text-3xl font-semibold tracking-tighter text-white sm:text-4xl">
              Want to work with us?
            </h2>
            <p className="mt-3 font-body text-white/55">{OFFICE.email}</p>
          </div>
          <Link
            to="/contact"
            className="group inline-flex w-max items-center gap-2 rounded-full bg-cyber px-7 py-4 font-body text-sm font-medium text-ink-950 transition-colors duration-300 hover:bg-white"
          >
            Get in touch
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </Link>
        </div>
      </Reveal>
    </section>
  );
}

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHeader />
      <Spotlight />
      <Manifesto />
      <StatsBand />
      <CTA />
    </div>
  );
}
